import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { GroupType } from 'models/flexible/GroupType';
import {
  adminCreateDischargeReason,
  adminDischargeReasonsQueryKeys,
  adminShowDischargeReason,
  adminUpdateDischargeReason,
} from 'services/api/admin/dischargeReasons';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { GroupTypeFields } from 'services/api/fields';
import { BodySmall } from 'styles/typography';

const schema = yup.object({ displayName: yup.string().required('Display name is required') });

type FormValues = {
  displayName: string;
  dischargedToGroupType: GroupType<GroupTypeFields> | null;
  selectedGroupTypes: GroupType<GroupTypeFields>[];
  infoMessage: string;
  warningMessage: string;
};

export default function EditDischargeReason() {
  const { id } = useParams();
  const isEditing = !!id;

  const form = useAdminForm({
    id,
    show: adminShowDischargeReason,
    create: adminCreateDischargeReason,
    update: adminUpdateDischargeReason,
    include: ['dischargedToGroupType', 'selectedGroupTypes'] as const,
    queryKey: adminDischargeReasonsQueryKeys,
    schema,
    toFormValues: (data): FormValues => ({
      displayName: data?.displayName ?? '',
      dischargedToGroupType: data?.dischargedToGroupType ? new GroupType(data.dischargedToGroupType) : null,
      selectedGroupTypes: (data?.selectedGroupTypes ?? []).map((gt) => new GroupType(gt)),
      infoMessage: data?.infoMessage ?? '',
      warningMessage: data?.warningMessage ?? '',
    }),
    toPayload: (values) => ({
      displayName: values.displayName ?? '',
      dischargedToGroupTypeId: values.dischargedToGroupType?.id ?? null,
      groupTypeIds: (values.selectedGroupTypes ?? []).map((gt) => gt.id),
      infoMessage: values.infoMessage ?? '',
      warningMessage: values.warningMessage ?? '',
    }),
    onCreateSuccess: { toast: 'Disposition successfully added!', navigate: '/admin/discharge-reasons' },
    onUpdateSuccess: { toast: 'Disposition successfully updated!', navigate: '/admin/discharge-reasons' },
    onError: { toast: 'Something went wrong. Please try again.' },
  });

  // --- Dropdown options (independent data fetches) ---

  const allGroupTypeOptions = useInfiniteOptions({
    queryKey: ['groupTypes', 'all'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc' },
  });

  const providerGroupTypeOptions = useInfiniteOptions({
    queryKey: ['groupTypes', 'provider'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc', type: 'provider' },
  });

  if (form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Discharge Disposition`} />

      <SectionHeader>Disposition Configuration</SectionHeader>

      <InputGroup title='Disposition Label' error={form.errors.displayName}>
        <Input
          value={form.values.displayName ?? ''}
          onChange={(e) => form.setValue('displayName', e.target.value)}
          placeholder='Type...'
          $hasError={!!form.errors.displayName}
        />
      </InputGroup>

      <HelpText>
        Indicate below if a group type should be linked to this disposition. Linking a group type will display a
        dropdown at time of discharge to select a group
      </HelpText>

      <InputGroup title='Linked Group Type (optional)'>
        <Select
          {...allGroupTypeOptions}
          value={form.values.dischargedToGroupType}
          onChange={(v) => form.setValue('dischargedToGroupType', v)}
          getOptionValue={(o) => o.id}
          getOptionLabel={(o) => o.displayName}
          placeholder='Select group type...'
          isClearable
        />
      </InputGroup>

      <DisplayRulesHeading>Display Rules</DisplayRulesHeading>

      <HelpText>Select below which group types this disposition should be displayed to at time of discharge</HelpText>

      <InputGroup>
        <MultiSelectChips
          title='Displayed To (select all that apply)'
          {...providerGroupTypeOptions}
          values={form.values.selectedGroupTypes}
          onChange={(v) => form.setValue('selectedGroupTypes', v)}
          getOptionValue={(o) => o.id}
          getOptionLabel={(o) => o.displayName}
          placeholder='All Group Types'
        />
      </InputGroup>

      <InputGroup title='Info Message (optional)'>
        <StyledTextarea
          value={form.values.infoMessage ?? ''}
          onChange={(e) => form.setValue('infoMessage', e.target.value)}
          placeholder='Type out an info message...'
        />
      </InputGroup>

      <InputGroup title='Warning Message (optional)'>
        <StyledTextarea
          value={form.values.warningMessage ?? ''}
          onChange={(e) => form.setValue('warningMessage', e.target.value)}
          placeholder='Type out a warning message...'
        />
      </InputGroup>

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={() => form.goBack()}>
          Cancel
        </Button>
        <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
          {isEditing ? 'Update Disposition' : 'Create Disposition'}
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const HelpText = styled(BodySmall)`
  margin-bottom: 20px;
`;

const DisplayRulesHeading = styled(SectionHeader)`
  margin-top: 24px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 80px;
  border: 1px solid var(--black-25);
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  line-height: 20px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }

  &::placeholder {
    color: var(--black-25);
  }
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
